import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import history from '../helpers/history';
import {getCall, postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import FormButton from '../component/atoms/FormButton';
import LinkButton from '../component/atoms/LinkButton';
import {checkUserRoles, getUser} from '../helpers/authentication';
import {validateCustomerId} from '../helpers/validate';
import {IMPORT_ROLES} from '../settings';
import {getUrlParams} from '../helpers/location';


const WorkspaceFormComponent = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30
}));

const FormTitleComponent = styled(Typography)({
    marginBottom: 20
});

const FormControlComponent = styled(FormControl)({
    marginBottom: 20
});

const ErrorMessageComponent = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 5,
    marginLeft: 15
}));

const WorkspaceMetadataComponent = styled(Grid)({
    marginTop: 5,
    marginBottom: 30
});


class WorkspaceForm extends React.Component {

    constructor(props) {
        super(props);

        this.params = getUrlParams();

        this.state = {
            workspace_id: this.params.id || null,
            name: '',
            description: '',
            customer_id: '',
            customer_id_exist: false,
            created: '',
            owner_name: '',
            form_errors: {},
            user: null
        };

        this.is_mounted = false;
        this.form_data = {};

        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.create_workspace_url = concatUrl(window.ROOT_URL, 'api', 'workspace', 'create');
        this.get_workspace_url = concatUrl(window.ROOT_URL, 'api', 'workspace', 'data');
        this.save_workspace_url = concatUrl(window.ROOT_URL, 'api', 'workspace', 'save');

        this.form_title = this.params.id ? 'Edit workspace' : 'Create workspace';
        this.save_button = this.params.id ? 'Save workspace' : 'Create workspace';

    }

    componentDidMount(){
        this.is_mounted = true;
        if(this.state.workspace_id){
            this.loadWorkspaceData();
        }else{
            this.context.checkUserAccess(this.conditionCallback);
        }
        getUser((user) => {
            if(this.is_mounted) {
                this.setState({user: user});
            }
        });
    }

    componentDidUpdate(){
        this.props.updatedCallback();
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    formValidated = () => {
        let form_errors = {};

        form_errors = {
            'name': this.state.name.trim().length === 0 ? 'Please enter a name' : '',
            'description': this.state.description.trim().length === 0 ? 'Please enter a description' : '',
            'customer_id': this.state.customer_id.trim().length === 0 ? null : validateCustomerId(this.state.customer_id)
        };

        this.form_data = {
            name: this.state.name.trim(),
            description: this.state.description.trim()
        };

        if (this.state.customer_id.trim() !== '' && !this.state.customer_id_exist){
            this.form_data['customer_id'] = this.state.customer_id.trim();
        }

        if (this.state.workspace_id){
            // edit existing workspace
            this.form_data['id'] = this.state.workspace_id;
        }

        this.setState({form_errors: form_errors});
        let form_is_valid = true;
        /*eslint-disable no-unused-vars */
        for (const key in form_errors){
            if (form_errors[key]){
                form_is_valid = false;
                break;
            }
        }
        /*eslint-enable no-unused-vars */

        return form_is_valid;
    };


    // +++ save workspace +++++++++++++++++++++++++++++++++++++++++++++++

    saveWorkspaceFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not save the workspace data.');
    };

    saveWorkspaceDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data && data.error){
            switch(data.error) {
                case 'NameExistsError':
                    this.setState({form_errors: {'name': data.error_msg}});
                    break;
                case 'CustomerIdExistsError':
                    this.setState({form_errors: {'customer_id': data.error_msg}});
                    break;
                case 'System':
                    callback_failure();
                    break;
            }
        }else{
            history.push('/workspaces');
            document.location.reload();
        }
    };

    saveWorkspace = () => {
        if (this.formValidated()){
            const url = this.state.workspace_id ? this.save_workspace_url : this.create_workspace_url;
            postCall(url, this.form_data, this.saveWorkspaceDone, this.saveWorkspaceFailed);
        }
    };


    // +++ load workspace data +++++++++++++++++++++++++++++++++++++++++++++++

    loadWorkspaceDataFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load workspace data.');
    };

    loadWorkspaceDataDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            this.setState({
                name: data.name,
                description: data.description,
                customer_id: data.customer_id || '',
                created: data.created,
                owner_name: data.owner_name,
                customer_id_exist: data.customer_id && data.customer_id.length > 0
            });
        }
    };

    loadWorkspaceData = () => {
        getCall(`${this.get_workspace_url}?id=${this.state.workspace_id}`, this.loadWorkspaceDataDone, this.loadWorkspaceDataFailed);
    };


    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <ErrorMessageComponent data-bt={`error_${key}`}>
                    {this.state.form_errors[key]}
                </ErrorMessageComponent>
            );
        }
    };

    errorMode = (key) => {
        return key in this.state.form_errors && this.state.form_errors[key] !== null;
    };

    addMetadata(){
        if (this.state.workspace_id){
            return(
                 <WorkspaceMetadataComponent container spacing={5}>
                    <Grid size={6}>
                        <Typography>
                            Created: {this.state.created}
                        </Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography>
                            Owner: {this.state.owner_name}
                        </Typography>
                    </Grid>
                </WorkspaceMetadataComponent>
            );
        }
    }

    customerId = () => {
        if (checkUserRoles(this.state.user, IMPORT_ROLES)){
            return (
                <Grid size={6}>
                    <FormControlComponent fullWidth={true}>
                        <TextField
                            error={this.errorMode('customer_id')}
                            disabled={this.state.customer_id_exist}
                            variant="outlined"
                            id="customer_id"
                            type="text"
                            label="Customer ID - can't be modified after it was set once"
                            slotProps={{ htmlInput: {'data-bt': 'customer_id'} }}
                            value={this.state.customer_id}
                            onChange={(event) => {
                                this.setState({customer_id: event.target.value});
                            }}
                        />
                        {this.showErrorMessage('customer_id')}
                    </FormControlComponent>
                </Grid>
            );
        }
    };

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <form>
                    <WorkspaceFormComponent data-bt="workspace_form">
                        <FormTitleComponent variant="h5">
                            {this.form_title}
                        </FormTitleComponent>

                        {this.addMetadata()}

                        <Grid container spacing={5}>
                            <Grid size={6}>
                                <FormControlComponent fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('name')}
                                        variant="outlined"
                                        id="name"
                                        type="text"
                                        label="Name"
                                        slotProps={{ htmlInput: {'data-bt': 'name'} }}
                                        value={this.state.name}
                                        onChange={(event) => {
                                            this.setState({name: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('name')}
                                </FormControlComponent>
                            </Grid>
                            <Grid size={12}>
                                <FormControlComponent fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('description')}
                                        variant="outlined"
                                        id="description"
                                        type="text"
                                        label="Description"
                                        slotProps={{ htmlInput: {'data-bt': 'description'} }}
                                        value={this.state.description}
                                        onChange={(event) => {
                                            this.setState({description: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('description')}
                                </FormControlComponent>
                            </Grid>
                            {this.customerId()}
                            <Grid size={12}>
                                <FormButton text={this.save_button} dataBt="create" actionCallback={this.saveWorkspace} />
                                <LinkButton text="Cancel" dataBt="cancel" linkData={{pathname: '/workspaces'}} />
                            </Grid>
                        </Grid>
                    </WorkspaceFormComponent>
                </form>
            )}
            </GlobalContext.Consumer>
        );
    }
}

WorkspaceForm.contextType = GlobalContext;

WorkspaceForm.propTypes = {
    updatedCallback: PropTypes.func
};

export default WorkspaceForm;
